import { styled } from '@mui/material';
import Image from 'next/image';
import React, { useEffect, useState, useRef } from 'react';
import Image1A from './assets/1A.webp';
import Image2A from './assets/2A.webp';
import Image3A from './assets/3A.webp';
import Image4A from './assets/4A.webp';
import Image5A from './assets/5A.webp';
import Image6A from './assets/6A.webp';
import Image7A from './assets/7A.webp';
import PauseDefaultImg from './assets/buttons/pause.png';
import PauseHoverImg from './assets/buttons/pause-hover.png';
import PlayDefaultImg from './assets/buttons/play.png';
import PlayHoverImg from './assets/buttons/play-hover.png';
import LeftImg from './assets/buttons/left.png';
import LeftHoverImg from './assets/buttons/left-hover.png';
import RightImg from './assets/buttons/right.png';
import RightHoverImg from './assets/buttons/right-hover.png';
import DotActiveImg from './assets/buttons/dot-active.png';
import DotDefaultImg from './assets/buttons/dot.png';

const CarouselContainer = styled('div')`
  position: relative;
  overflow: hidden;
  top: 50px;
`;

const CarouselWrapper = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CarouselImageWrapper = styled('div')`
  position: relative;
  width: 100%;
  max-height: 55vh;
  aspect-ratio: 1;
`;

const CarouselImage = styled(Image)`
  width: 100%;
  height: auto;
  max-height: 55vh;
  aspect-ratio: 1;
  object-fit: contain;
  transition: opacity 1s ease-in-out;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
`;

const Controls = styled('div')`
  position: absolute;
  display: flex;
  align-items: center;
  margin-top: -15px;
  margin-bottom: -15px;
`;

const ArrowButton = styled('button')<{ direction?: 'left' | 'right' }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 10px;
  position: relative;

  .arrow-default {
    transition: opacity 0.3s ease-in-out;
  }

  .arrow-hover {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover .arrow-default {
    opacity: 0;
  }

  &:hover .arrow-hover {
    opacity: 1;
  }
`;

const Dots = styled('div')`
  display: flex;
  align-items: center;
`;

const Dot = styled('div')<{ active: boolean }>`
  width: ${props => (props.active ? '24px' : '12px')};
  height: ${props => (props.active ? '24px' : '12px')};
  margin: 0 5px;
  background: ${props =>
    props.active ? `url('${DotActiveImg.src}')` : `url('${DotDefaultImg.src}')`};
  background-size: cover;
  scale: ${props => (props.active ? 1.5 : 1)};
  cursor: pointer;

  &:hover {
    background: url('${DotActiveImg.src}');
    background-size: cover;
    scale: ${props => (props.active ? 1.5 : 2.5)};
  }
`;

const PauseButton = styled('button')`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const images = [Image1A, Image2A, Image3A, Image4A, Image5A, Image6A, Image7A];

export default function HeaderCarousel() {
  const [mainHover, setMainHover] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(
    Math.floor(Math.random() * images.length),
  );
  const [isPaused, setIsPaused] = useState<boolean>(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(nextSlide, 5000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused]);

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  return (
    <CarouselContainer>
      <CarouselWrapper>
        <CarouselImageWrapper>
          {images.map((img, idx) => (
            <CarouselImage
              key={idx}
              src={img}
              alt="carousel"
              style={{ opacity: idx === currentIndex ? 1 : 0 }}
            />
          ))}
        </CarouselImageWrapper>
        <Controls style={{ position: 'static', marginTop: '10px' }}>
          <ArrowButton onClick={prevSlide} direction="left">
            <div className="arrow-default">
              <Image width={64} height={64} src={LeftImg} alt="Previous" />
            </div>
            <div className="arrow-hover">
              <Image width={64} height={64} src={LeftHoverImg} alt="Previous" />
            </div>
          </ArrowButton>
          <Dots>
            {images.map((_, index) => (
              <Dot key={index} active={index === currentIndex} />
            ))}
          </Dots>
          <ArrowButton onClick={nextSlide} direction="right">
            <div className="arrow-default">
              <Image width={64} height={64} src={RightImg} alt="Next" />
            </div>
            <div className="arrow-hover">
              <Image width={64} height={64} src={RightHoverImg} alt="Next" />
            </div>
          </ArrowButton>
        </Controls>
        <PauseButton
          onClick={togglePause}
          style={{ position: 'static', marginTop: '10px' }}
          onMouseEnter={() => setMainHover(true)}
          onMouseLeave={() => setMainHover(false)}
        >
          {isPaused ? (
            mainHover ? (
              <Image width={104} height={33} src={PlayHoverImg} alt="Play" />
            ) : (
              <Image width={104} height={33} src={PlayDefaultImg} alt="Play" />
            )
          ) : mainHover ? (
            <Image width={104} height={33} src={PauseHoverImg} alt="Pause" />
          ) : (
            <Image width={104} height={33} src={PauseDefaultImg} alt="Pause" />
          )}
        </PauseButton>
      </CarouselWrapper>
    </CarouselContainer>
  );
}
